import type { VirtualItem } from '@tanstack/react-virtual';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button, Checkbox, LinkButton, useMountEffect } from '@timelyapp/react';
import DatePickerDropdown from 'components/datepicker/DatePickerDropdown';
import ProjectFilterDropdown from 'components/dropdowns/ProjectFilterDropdown';
import TeamsSection from 'components/editors/user-editor/TeamsSection';
import UserEditorProject from 'components/editors/user-editor/UserEditorProject';
import Form from 'components/Form';
import InlineMessage from 'components/InlineMessage';
import Input from 'components/Input';
import InputDecorator from 'components/InputDecorator';
import InputHelpText from 'components/InputHelpText';
import InputLabel from 'components/InputLabel';
import NumberInput from 'components/NumberInput';
import Overlay from 'components/Overlay';
import { Radio } from 'components/Radio';
import { RadioGroup } from 'components/RadioGroup';
import type { ScrollbarsRefType } from 'components/scrollbars';
import { Scrollbars } from 'components/scrollbars';
import Tooltip from 'components/Tooltip';
import { Container, ContainerContent, ContainerSiderbar } from 'components/ui-blocks/container';
import {
  FormSection,
  SectionColumn,
  SectionDetails,
  SectionGrid,
  SectionRow,
} from 'components/ui-blocks/form/FormSection';
import { Header, HeaderGroup, HeaderRow } from 'components/ui-blocks/header';
import WeekdaySelector from 'components/WeekdaySelector';
import { MAX_WEEKLY_HOURS } from 'constants/account';
import { HAS_CREATED_USER } from 'constants/user';
import { useSequentialCallback } from 'hooks/useSequentialCallback';
import { DateTime } from 'luxon';
import React, { useCallback, useRef, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import { connect, useSelector } from 'react-redux';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import {
  addProjectToDraft,
  applyDefaultRateForInvitedUser,
  clearDraft,
  fetchAddedProjectsCountForClientInEditorDropdown,
  removeAllProjectsFromDraft,
  removeProjectFromDraft,
  removeTeamFromUser,
  saveUserDraft,
  searchProjectsForEditorDropdown,
  setProjectRatesFilter,
  toggleClientOnDraft,
  updateDraft,
  updateDraftDefaultHourRate,
  updateDraftDisableAddToAllProjects,
  updateDraftInternalCostRate,
  updateDraftProjectRate,
  updateDraftRoleId,
  updateDraftSelectedTeams,
} from 'redux/modules/user.editor.actions';
import { InternalCostRateChange } from 'redux/modules/user.editor.types';
import { getAccountFeature, getCurrentAccount } from 'redux/selectors/accounts';
import { getUserEditorData } from 'redux/selectors/user.editor';
import type { IState } from 'redux/state';
import type { RateType } from 'types/project';
import type { MappedActions } from 'types/redux';
import { RoleName } from 'types/role';
import { formatCurrency } from 'utils/currency';
import { notNull } from 'utils/typesafe';
import { useViewportWidthBreakpoint, ViewportWidthBreakpoint } from 'utils/viewport';
import styles from './UserEditor.module.scss';
import ProjectRateChangeWarning from './user-editor/ProjectRateChangeWarning';
import RateTypeDropdown from './user-editor/RateTypeDropdown';
import UserAccountPropertiesSection from './user-editor/UserAccountPropertiesSection';

const mapStateToProps = (state: IState) => getUserEditorData(state);
const mapActionsToProps = {
  addProjectToDraft,
  clearDraft,
  removeAllProjectsFromDraft,
  removeProjectFromDraft,
  removeTeamFromUser,
  saveUserDraft,
  saveUserProperty,
  searchProjectsForEditorDropdown,
  fetchAddedProjectsCountForClientInEditorDropdown,
  setProjectRatesFilter,
  showSubscriptionNoticeModal,
  toggleClientOnDraft,
  updateDraft,
  updateDraftProjectRate,
  updateDraftDefaultHourRate,
  updateDraftInternalCostRate,
  updateDraftRoleId,
  updateDraftSelectedTeams,
  updateDraftDisableAddToAllProjects,
  applyDefaultRateForInvitedUser,
};

interface ExternalProps {
  onSubmit?: () => void;
}

type ActionProps = MappedActions<typeof mapActionsToProps>;

type Props = ReturnType<typeof getUserEditorData> & ActionProps & ExternalProps;

const UserEditor: React.FC<Props> = props => {
  const {
    clearDraft,
    currency,
    currencyLookup,
    draft,
    dropdown,
    ratesList,
    removeProjectFromDraft,
    removeTeamFromUser,
    selectedTeamIds,
    teams,
    ui,
    updateDraft,
    updateDraftProjectRate,
    updateDraftSelectedTeams,
    updateDraftDisableAddToAllProjects,
    userRoles,
    searchProjectsForEditorDropdown: searchProjectsForDropdown,
    fetchAddedProjectsCountForClientInEditorDropdown: fetchAddedProjectsCount,
    teamAssociationsByProjectId,
  } = props;
  const isUserCustomPropertiesFeatureEnabled = useSelector((state: IState) =>
    getAccountFeature(state, 'user_custom_properties')
  );
  const hasIndividualCapacityFeature = useSelector((state: IState) =>
    getAccountFeature(state, 'individual_capacity')
  );
  const parentRef = useRef<ScrollbarsRefType | null>(null);
  // project row height + margin
  const rowHeight = 32 + 12;

  const rowVirtualizer = useVirtualizer({
    count: ratesList.projects.length,
    getScrollElement: () => parentRef.current?.view as Element | null,
    estimateSize: () => rowHeight,
  });
  const [showExternalId, setShowExternalId] = useState(!!draft?.external_id);
  const [showProjectRateWarning, setShowProjectRateWarning] = useState<boolean | null>(null);
  const [projectIdForRateChange, setProjectIdForRateChange] = useState<number | null>(null);
  const [internalCostRateSetting, setInternalCostRateSetting] =
    useState<InternalCostRateChange | null>(null);
  const account = useSelector(getCurrentAccount);
  const currentWidthBreakpoint = useViewportWidthBreakpoint();
  const showSidebar = currentWidthBreakpoint > ViewportWidthBreakpoint.Medium;

  useMountEffect(() => {
    if (!draft?.id) {
      userRoles.forEach(role => {
        role.scopes.forEach(scope => {
          updateDraft({ [scope.name]: scope.default });
        });
      });
    }
  });

  const handleSubmit = (): void => {
    if (!draft || ui.isSaveDisabled) return;

    const save = props.saveUserDraft(draft, {
      clearDraft: true,
      addTeamUsersToProjects: true,
      removeTeamUsersFromProjects: true,
    });

    if (props.onSubmit) {
      save.then(() => {
        props.saveUserProperty(HAS_CREATED_USER);
        props.onSubmit?.();
      });
    }
  };

  const handleSearchProjectsForEditorDropdown = useCallback(
    (query: string, _: any, tagId?: number) =>
      searchProjectsForDropdown(query, undefined, tagId ? [tagId] : undefined),
    [searchProjectsForDropdown]
  );

  const handleClickLockedFeature = () => {
    if (!ui.isIndividualUserCapacityFeatureEnabled) {
      props.showSubscriptionNoticeModal('featureIndividualCapacityRequiresUpdate');
    }
  };

  const handleCancel = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    clearDraft();
  };

  const handleInputChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const { name, checked, value, type } = event.currentTarget;

    const payload = {
      [name]: type === 'checkbox' ? !!checked : value,
    };

    updateDraft(payload);
  };

  const handleRoleInputChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const { value } = event.currentTarget;
    props.updateDraftRoleId(+value);
  };

  const handleUpdateCapacity = (hours: number) => {
    if (hours < 0) {
      updateDraft({ weekly_capacity: 0 });
      return;
    }
    const weekly_capacity =
      hours >= MAX_WEEKLY_HOURS ? MAX_WEEKLY_HOURS : Math.round(hours * 2) / 2;
    updateDraft({ weekly_capacity });
  };

  const updateWeekdays = (work_days: string) => {
    updateDraft({ work_days });
  };

  const handleUpdateDefaultHourRate = (value: number): void => {
    props.updateDraftDefaultHourRate(value);
  };

  const handleUpdateInternalCostRate = (value: number): void => {
    props.updateDraftInternalCostRate(value);
  };

  const handleTriggerLockedFeature = () => {
    if (!ui.isHourlyRateFeatureEnabled) {
      props.showSubscriptionNoticeModal('featureHourlyRatesRequiresUpgrade');
    }
  };

  const handleLoadMoreForClient = useCallback(
    (clientId: number) => {
      searchProjectsForDropdown(undefined, clientId);
    },
    [searchProjectsForDropdown]
  );

  const handleRenderClients = useSequentialCallback((clientIds: Array<number>) =>
    fetchAddedProjectsCount(clientIds)
  );

  const handleUpdateDraftProjectRate = (updatedProject: {
    project_id: number;
    hour_rate: number;
  }) => {
    const payload = {
      ...updatedProject,
      update_existing_hours: true,
      update_unbilled_only: true,
    };
    updateDraftProjectRate(payload);
  };

  const handleProjectInputFocus = (projectId: number) => {
    if (showProjectRateWarning == null) {
      setShowProjectRateWarning(true);
      setProjectIdForRateChange(projectId);
    }
  };

  const renderPermissionsListRow = ({ key, index }: Pick<VirtualItem, 'key' | 'index'>) => {
    const row = ratesList.projects[index];
    const projectCurrency = currencyLookup[row.project.currency_code];
    return (
      <div key={key} className={styles.clientsProjectsRow}>
        <UserEditorProject
          onClickDisabledFeature={handleTriggerLockedFeature}
          isHourlyRateFeatureEnabled={ui.isHourlyRateFeatureEnabled}
          className={styles.clientProject}
          permission={row}
          currency={projectCurrency}
          onChange={handleUpdateDraftProjectRate}
          onRemove={removeProjectFromDraft}
          onFocus={handleProjectInputFocus}
          teamAssociationsByProjectId={teamAssociationsByProjectId}
        />
      </div>
    );
  };

  const onChangePermissionFilterString = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    props.setProjectRatesFilter({ queryString: value });
  };
  const onChangeRateTypeFilter = (rateType: RateType | null) => {
    props.setProjectRatesFilter({ rateType });
  };
  function renderPermissionsList() {
    if (!draft) return null;

    if (!ratesList.hasInitialized) {
      return (
        <div className={styles.emptyListMessage}>
          <p>Loading...</p>
        </div>
      );
    }

    if (dropdown.numSelectedProjects < 1) {
      return (
        <div className={styles.emptyListMessage}>
          <p>No Projects Added</p>
          <br />
          <p>This user will be unable to use Timely</p>
        </div>
      );
    }

    if (!ratesList.projects.length) {
      if (ratesList.filters.queryString.length > 0 || ratesList.filters.rateType != null) {
        if (ratesList.filters.queryString.length < 2) {
          return (
            <InlineMessage type="info" title="No results">
              Try other search terms.
            </InlineMessage>
          );
        }
        return (
          <InlineMessage type="warning" title="No results">
            {draft.name || 'This person'} is not added to any such projects
          </InlineMessage>
        );
      }
      return (
        <InlineMessage
          type="warning"
          title={`No projects assigned to ${draft.name || 'this person'}`}
        >
          They won't be able to use Timely if they aren't assigned to any projects.
        </InlineMessage>
      );
    }
    return (
      <Scrollbars ref={parentRef} style={{ height: 250 }}>
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map(({ index, key, size, start }) => {
            return (
              <div
                key={index}
                style={{
                  position: 'absolute',
                  top: start,
                  left: 0,
                  width: '100%',
                  height: `${size}px`,
                }}
              >
                {renderPermissionsListRow({ index, key })}
              </div>
            );
          })}
        </div>
      </Scrollbars>
    );
  }

  const handleApplyDefaultRate = () => {
    props.applyDefaultRateForInvitedUser();
  };

  // The billing options that reprice existing entries — the only ones where a
  // date floor is meaningful. Single source for both the picker's visibility
  // and clearing the date when a no-history option is picked, so the two
  // sites cannot drift apart when an option is added or renamed.
  const touchesExistingEntries = (value: 'no' | 'all' | 'all_without_history' | 'matching') =>
    value === 'all' || value === 'matching';

  // TT-5860: optional date floor for a rate reprice — only entries dated on or
  // after the chosen day get the new rate. Rendered under the billing and cost
  // radio groups whenever the selected option touches existing entries.
  const renderEffectiveFromPicker = (
    value: string | null | undefined,
    onChange: (date: string | null) => void,
    testId: string
  ) => (
    <div style={{ marginTop: 12 }}>
      <InputLabel>Apply from date (optional)</InputLabel>
      {!value ? (
        // "All time" is the current STATE, shown as text — the button beside it
        // is the control that ends it by switching to a concrete date (today)
        // that the picker below then adjusts. Labelling the button itself
        // "All time" inverted the affordance: one click on it floored the
        // reprice to today (PR review). The dropdown itself must not render in
        // this state: it commits its baseDate on every close, so a mere
        // dismissal would write today.
        <>
          <span data-testid={`${testId}-state`} style={{ marginRight: 8 }}>
            All time
          </span>
          <Button data-testid={testId} onClick={() => onChange(DateTime.now().toISODate())}>
            <FontAwesome name="calendar" />
            <span>Choose a start date</span>
          </Button>
          <InputHelpText>
            Choose a date to only apply the new rate to entries on or after it.
          </InputHelpText>
        </>
      ) : (
        <>
          {/* Keyed by value: day mode has no auto-close, so remount (closed)
              once a new date is committed. The wrapper span carries the
              panel-specific test id — the dropdown's own target id is shared
              and duplicates when both rate panels are open. */}
          <span data-testid={`${testId}_picker`}>
            <DatePickerDropdown
              key={value}
              initialMode="day"
              onDateChange={date => {
                if (date !== value) onChange(date);
              }}
              startOfWeek={account.start_of_week}
              baseDate={value}
              // A dateRange is required for the day grid to honour disabledAfter
              // at all (DatePicker.getDisabledDays only builds disabled days
              // when a range is present); a single-day range is enough to
              // disable future dates while leaving past days pickable.
              dateRange={{ from: value, to: value }}
              disabledAfter={DateTime.now().toISODate()}
            />
          </span>
          <Button data-testid={`${testId}_reset`} onClick={() => onChange(null)}>
            Reset to all time
          </Button>
          <InputHelpText>
            Only entries on or after this date get the new rate. Entries added later but dated
            before it will still use the new rate.
          </InputHelpText>
        </>
      )}
    </div>
  );

  const renderDefaultRateApplicationSettings = () => {
    if (draft?.id == null) {
      return (
        <InlineMessage type="info" title="Default billable rate changed" style={{ marginTop: 16 }}>
          <p style={{ paddingTop: 10, paddingBottom: 10 }}>
            Some of the projects you have selected have individual billable rates. Click the button
            below if you would like to override them with this new default rate.
          </p>
          <Button id="apply_default_rate_for_invited_user" onClick={handleApplyDefaultRate}>
            Apply default rate
          </Button>
        </InlineMessage>
      );
    }

    const { default_hour_rate, initial_default_hour_rate } = draft;
    // Hide the whole panel once the rate is back to its original value —
    // reverting the number should take the radios and date picker with it, even
    // if a stale apply_default_rate is still sitting in the draft (the request
    // already strips it). Guarding on apply_default_rate left them on screen.
    if (default_hour_rate === initial_default_hour_rate) {
      return null;
    }
    const { apply_default_rate } = draft;

    const options = [
      <Radio key="no" value={'no' as const} label="New projects only" />,
      <Radio
        key="all"
        value={'all' as const}
        label="All projects - all entries"
        description="Will apply to all unbilled and not locked entries"
      />,
      <Radio
        key="all_without_history"
        value={'all_without_history' as const}
        label="All projects - new entries"
        description="Will only apply to new entries"
      />,
      <Radio
        key="matching"
        value={'matching' as const}
        label="Projects with the previous billable rate"
        description={`Will only apply to projects and entries (unbilled and not locked) where the billable rate is ${formatCurrency(
          account.currency,
          initial_default_hour_rate
        )}`}
      />,
    ].filter(notNull);

    const handleClickOption = (value: 'no' | 'all' | 'all_without_history' | 'matching') => {
      props.updateDraft({
        apply_default_rate: value,
        // Only options that touch existing entries can carry a date boundary.
        ...(touchesExistingEntries(value) ? {} : { hour_rate_effective_from: null }),
      });
    };

    const showBillingEffectiveFrom =
      apply_default_rate != null && touchesExistingEntries(apply_default_rate);

    return (
      <InlineMessage
        type={apply_default_rate == null ? 'warning' : 'info'}
        title="Default billable rate changed"
        style={{ marginTop: 16 }}
      >
        <p style={{ paddingTop: 10, paddingBottom: 10 }}>
          Which projects should this change be applied to?
        </p>
        <RadioGroup
          name="apply_default_rate"
          onChange={handleClickOption}
          selectedValue={apply_default_rate ?? undefined}
        >
          {options}
        </RadioGroup>
        {showBillingEffectiveFrom &&
          renderEffectiveFromPicker(
            draft.hour_rate_effective_from,
            date => props.updateDraft({ hour_rate_effective_from: date }),
            'billing_rate_effective_from'
          )}
      </InlineMessage>
    );
  };

  const renderInternalHourRateSettings = () => {
    if (!account.features.internal_costs) {
      return null;
    }
    // doesn't apply to new users
    if (!draft?.id) {
      return null;
    }
    const options = [
      <Radio
        key="internal_new"
        value={InternalCostRateChange.NewOnly}
        label="New entries only"
        description="Existing entries will remain unchanged."
      />,
      <Radio
        key="internal_all"
        value={InternalCostRateChange.All}
        label="All entries"
        description="Billed, unbilled, and new will assume the new rate."
      />,
    ].filter(notNull);

    const onChangeInternalCostSetting = (value: InternalCostRateChange) => {
      if (value === InternalCostRateChange.NewOnly) {
        props.updateDraft({
          update_existing_hours_internal_rate: false,
          update_unbilled_hours_internal_rate: false,
          // "New entries only" touches no history, so a date boundary is moot.
          internal_hour_rate_effective_from: null,
        });
      }
      if (value === InternalCostRateChange.All) {
        props.updateDraft({
          update_existing_hours_internal_rate: true,
          update_unbilled_hours_internal_rate: false,
        });
      }
      setInternalCostRateSetting(value);
    };

    return (
      <InlineMessage
        type={internalCostRateSetting == null ? 'warning' : 'info'}
        title="Cost rate has changed"
        style={{ marginTop: 16 }}
      >
        <p style={{ paddingTop: 10, paddingBottom: 10 }}>
          Should all entries for this person use the new rate?
        </p>
        <RadioGroup
          name="change_internal_cost_rate"
          onChange={onChangeInternalCostSetting}
          selectedValue={internalCostRateSetting ?? undefined}
        >
          {options}
        </RadioGroup>
        {internalCostRateSetting === InternalCostRateChange.All &&
          renderEffectiveFromPicker(
            draft.internal_hour_rate_effective_from,
            date => props.updateDraft({ internal_hour_rate_effective_from: date }),
            'internal_rate_effective_from'
          )}
      </InlineMessage>
    );
  };

  const handleSelectAll = useCallback(() => {
    updateDraft({ add_to_all_projects: true });
  }, [updateDraft]);

  if (!draft) {
    return null;
  }

  return (
    <Overlay
      header={
        <Header alignToContainer withRows>
          <HeaderRow>
            <HeaderGroup alignContent="nearBottom">
              <Button onClick={handleCancel} disabled={ui.isFormDisabled}>
                <FontAwesome name="arrow-left" />
                <span>Back</span>
              </Button>
            </HeaderGroup>
            <HeaderGroup autoGrow />
            <HeaderGroup alignContent="nearBottom">
              <Button
                data-testid="save_user_editor"
                priority="xhigh"
                disabled={ui.isSaveDisabled}
                isLoading={ui.isSaving}
                onClick={handleSubmit}
              >
                {ui.labels.submitButton}
              </Button>
            </HeaderGroup>
          </HeaderRow>
          <HeaderRow>
            <HeaderGroup autoGrow>
              <h1>{ui.labels.header}</h1>
            </HeaderGroup>
          </HeaderRow>
        </Header>
      }
    >
      <Form onSubmit={handleSubmit}>
        <Container sidebar={showSidebar ? 'right' : undefined}>
          <ContainerContent>
            {ui.showUserLimitWarning && (
              <InlineMessage
                type="warning"
                title="You’ve filled all the seats in your plan, so you will be charged for any new user."
              />
            )}
            <FormSection>
              <SectionDetails>
                <h3>Basic Info</h3>
                <p>Enter the user’s details. (Note: the user name must not include numerals).</p>
              </SectionDetails>
              <SectionGrid>
                <SectionRow>
                  <SectionColumn size="auto">
                    <InputLabel>Name</InputLabel>
                    <Input
                      hideFromFullstory
                      autoFocus
                      required
                      size="medium"
                      name="name"
                      placeholder="What is this person called?"
                      value={draft.name}
                      onChange={handleInputChange}
                      disabled={ui.isBasicInfoFormDisabled}
                    />
                  </SectionColumn>
                </SectionRow>
                <SectionRow>
                  <SectionColumn size="auto">
                    <InputLabel>Email</InputLabel>
                    <Input
                      hideFromFullstory
                      required
                      name="email"
                      type="email"
                      placeholder="Primary work email for this person"
                      value={draft.email}
                      onChange={handleInputChange}
                      disabled={ui.isBasicInfoFormDisabled}
                    />
                  </SectionColumn>
                </SectionRow>
                <SectionRow>
                  {showExternalId ? (
                    <SectionColumn size="auto">
                      <InputLabel>External ID</InputLabel>
                      <Input
                        className="fs-mask"
                        name="external_id"
                        placeholder="Reference to external system (optional)"
                        value={draft.external_id || undefined}
                        onChange={handleInputChange}
                      />
                    </SectionColumn>
                  ) : (
                    <SectionColumn size="auto">
                      <Checkbox
                        data-testid="enable_external_id"
                        onChangeChecked={() => setShowExternalId(true)}
                        label="Set external ID reference"
                      />
                    </SectionColumn>
                  )}
                </SectionRow>
                {isUserCustomPropertiesFeatureEnabled && (
                  <UserAccountPropertiesSection userId={draft?.id} />
                )}
              </SectionGrid>
            </FormSection>
            <FormSection className={styles.roleSection}>
              <SectionDetails>
                <h3>Access level</h3>
                <p>Define what the user is allowed to see.</p>
              </SectionDetails>
              <SectionGrid>
                <SectionRow className={styles.roleSectionRow}>
                  <SectionColumn size="auto">
                    {userRoles
                      .filter(role => role.allowed_to_invite)
                      .map(roleOption => {
                        const isSelected = draft.role_id === roleOption.id;
                        return (
                          <React.Fragment key={roleOption.id}>
                            <Radio
                              name="role_id"
                              label={roleOption.display_name}
                              description={roleOption.description}
                              checked={isSelected}
                              disabled={ui.isFormDisabled}
                              onChange={handleRoleInputChange}
                              value={roleOption.id}
                              className={styles.roleOption}
                            />
                            {!!isSelected &&
                              roleOption.scopes
                                // Scopes that only have 1 option aren't changeable, so we hide them
                                .filter(scope => scope.options?.length > 1)
                                .map(scope => {
                                  let title: string | undefined;

                                  const isInternalHourlyRateScope =
                                    scope.name === 'hide_internal_hourly_rate' &&
                                    !!draft[scope.name];
                                  const managerRoleTitle = `Unchecking this option will allow ${
                                    draft.name || 'this user'
                                  } to see the internal cost rate of people they share projects with`;

                                  const employeeRoleTitle = `Unchecking this option will allow ${
                                    draft.name || 'this user'
                                  } to see their own internal cost rate`;

                                  if (isInternalHourlyRateScope) {
                                    if (roleOption.name === RoleName.Manager) {
                                      title = managerRoleTitle;
                                    } else if (roleOption.name === RoleName.Employee) {
                                      title = employeeRoleTitle;
                                    }
                                  }

                                  return (
                                    <Tooltip title={title} key={scope.name}>
                                      <InputLabel className={styles.subgroup}>
                                        <Checkbox
                                          name={scope.name}
                                          checked={!!draft[scope.name]}
                                          disabled={ui.isFormDisabled}
                                          onChange={handleInputChange}
                                          label={scope.display_name}
                                        />
                                      </InputLabel>
                                    </Tooltip>
                                  );
                                })}
                          </React.Fragment>
                        );
                      })}
                    {ui.hasLivePublicReports && (
                      <InlineMessage
                        style={{ marginTop: 20, marginBottom: 0 }}
                        type="warning"
                        title="Updating this user’s access level may also change the data available in the live reports they have shared."
                      />
                    )}
                  </SectionColumn>
                </SectionRow>
              </SectionGrid>
            </FormSection>
            <FormSection>
              <SectionDetails>
                <h3>Capacity & Working days</h3>
                <p>Set availability and working days for this person</p>
              </SectionDetails>
              <SectionGrid>
                <SectionColumn size="half" flex="initial">
                  <InputLabel>Capacity</InputLabel>
                  <InputDecorator
                    inputType="number"
                    isLockedFeature={!ui.isIndividualUserCapacityFeatureEnabled}
                    onClick={handleClickLockedFeature}
                    size="medium"
                    suffix="h/wk"
                  >
                    <NumberInput
                      selectOnFocus
                      disabled={ui.isFormDisabled || !hasIndividualCapacityFeature}
                      readOnly={!ui.isIndividualUserCapacityFeatureEnabled}
                      size="medium"
                      name="default_capacity"
                      max={MAX_WEEKLY_HOURS}
                      min={1}
                      step={0.5}
                      value={draft.weekly_capacity}
                      onChange={handleUpdateCapacity}
                    />
                  </InputDecorator>
                  <InputHelpText>Individual capacity overrides workspace settings</InputHelpText>
                </SectionColumn>
                <SectionColumn>
                  <div style={{ marginTop: 20 }}>
                    <InputLabel>Working Days</InputLabel>
                    <WeekdaySelector
                      type="short"
                      workdays={draft.work_days}
                      onChange={updateWeekdays}
                      disabled={!hasIndividualCapacityFeature}
                    />
                    <InputHelpText style={{ marginTop: 3 }}>
                      Individual working days override workspace settings
                    </InputHelpText>
                  </div>
                </SectionColumn>
              </SectionGrid>
            </FormSection>
            <FormSection>
              <SectionDetails>
                <h3>Rates</h3>
                <p>Set default billable rate for this person</p>
              </SectionDetails>
              <SectionGrid>
                <SectionRow spaceBetween>
                  <SectionColumn size="half">
                    <InputLabel>Default billable rate</InputLabel>
                    <InputDecorator
                      inputType="number"
                      onClick={handleTriggerLockedFeature}
                      isLockedFeature={!ui.isHourlyRateFeatureEnabled}
                      size="medium"
                      suffix={`${currency.symbol}/hr`}
                    >
                      <NumberInput
                        selectOnFocus
                        disabled={!ui.isHourlyRateFeatureEnabled || ui.isFormDisabled}
                        size="medium"
                        name="default_hour_rate"
                        value={draft.default_hour_rate}
                        onChange={handleUpdateDefaultHourRate}
                        min={0}
                      />
                    </InputDecorator>
                    <InputHelpText>
                      Set custom rates below for projects that support "individual rates".
                    </InputHelpText>
                    {renderDefaultRateApplicationSettings()}
                  </SectionColumn>
                  {account.features.internal_costs && (
                    <SectionColumn size="half">
                      <InputLabel>Cost rate</InputLabel>
                      <InputDecorator
                        inputType="number"
                        onClick={handleTriggerLockedFeature}
                        isLockedFeature={!ui.isHourlyRateFeatureEnabled}
                        size="medium"
                        suffix={`${currency.symbol}/hr`}
                      >
                        <NumberInput
                          selectOnFocus
                          // todo, move onto ui key
                          disabled={!account.features.internal_costs || ui.isFormDisabled}
                          size="medium"
                          name="internal_hour_rate"
                          value={draft.internal_hour_rate}
                          onChange={handleUpdateInternalCostRate}
                          min={0}
                        />
                      </InputDecorator>
                      <InputHelpText>Set the hourly labor cost for this individual.</InputHelpText>
                      {ui.hasChangedInternalHourRate && renderInternalHourRateSettings()}
                    </SectionColumn>
                  )}
                </SectionRow>
              </SectionGrid>
            </FormSection>
            <FormSection>
              <SectionDetails>
                <h3>Projects and Hourly Rates</h3>
                <p>Select projects the user can log time to.</p>
              </SectionDetails>
              <SectionGrid>
                {draft.add_to_all_projects ? (
                  <SectionRow>
                    <p>
                      This user will be added to{' '}
                      <strong>all {dropdown.numTotalProjects} projects</strong> in this workspace
                      <br />
                      <br />
                      <Button onClick={() => updateDraftDisableAddToAllProjects()}>Edit</Button>
                    </p>
                  </SectionRow>
                ) : (
                  <>
                    <SectionRow spaceBetween>
                      <SectionColumn size="auto">
                        <ProjectFilterDropdown
                          projects={dropdown.projects}
                          clients={dropdown.clients}
                          selected={dropdown.selectedIds}
                          numTotalProjects={dropdown.numTotalProjects}
                          numActiveProjects={dropdown.numActiveProjects}
                          isLoading={dropdown.isLoading}
                          projectFetchingStatus={dropdown.projectFetchingStatus}
                          highlightModified={false}
                          emptyLabel="Choose projects to add"
                          disabled={ui.isFormDisabled}
                          allowSelectNone
                          onSelectAll={handleSelectAll}
                          onSelectProject={props.addProjectToDraft}
                          onDeselectProject={props.removeProjectFromDraft}
                          onToggleClient={props.toggleClientOnDraft}
                          onClear={props.removeAllProjectsFromDraft}
                          onSearch={handleSearchProjectsForEditorDropdown}
                          showProjectTotalsOnClients
                          selectionPartiallyLoaded={draft.id != null}
                          onLoadMoreForClient={handleLoadMoreForClient}
                          onRenderClients={handleRenderClients}
                          numSelected={dropdown.numSelectedProjects}
                          numSelectedByClient={dropdown.numSelectedProjectsByClient}
                          tags={dropdown.tags}
                          projectIdsByTagId={dropdown.projectIdsByTagId}
                        />
                      </SectionColumn>
                      <div className={styles.permissionFilterColumn}>
                        <RateTypeDropdown
                          value={ratesList.filters.rateType}
                          onChange={onChangeRateTypeFilter}
                        />
                      </div>
                      <div className={styles.permissionFilterColumn}>
                        <Input
                          className={styles.permissionFilterInput}
                          value={ratesList.filters.queryString}
                          onChange={onChangePermissionFilterString}
                          placeholder="Client or Project"
                        />
                      </div>
                    </SectionRow>
                    <div className={styles.projectHeader}>
                      <span>Project</span>
                      <span>Project billable rate</span>
                    </div>
                    <SectionRow direction="column">
                      <SectionColumn size="auto">
                        <div style={{ maxHeight: 250, paddingTop: 10 }}>
                          {renderPermissionsList()}
                        </div>
                      </SectionColumn>
                    </SectionRow>
                  </>
                )}
              </SectionGrid>
            </FormSection>
            {ui.isTeamsFeatureEnabled && (
              <TeamsSection
                canEditTeams={ui.canEditTeams}
                draft={draft}
                teams={teams}
                selectedTeamIds={selectedTeamIds}
                onChangeSelectedTeams={updateDraftSelectedTeams}
                onRemoveTeam={removeTeamFromUser}
                disabled={ui.isFormDisabled}
              />
            )}
          </ContainerContent>
          {showSidebar && (
            <ContainerSiderbar side="right">
              <InlineMessage type="info" title="How to invite a person">
                <p>
                  Invite people to Timely and customize their access levels, default hourly rate,
                  and project assignments, and more.
                </p>
                <br />
                <LinkButton
                  size="small"
                  priority="low"
                  linkTo="https://support.memory.ai/en/articles/3364976-add-new-users-in-timely"
                  newWindow
                >
                  Get help
                </LinkButton>
              </InlineMessage>
            </ContainerSiderbar>
          )}
        </Container>
      </Form>
      <ProjectRateChangeWarning
        projectId={projectIdForRateChange}
        isOpen={!!showProjectRateWarning}
        onClose={() => setShowProjectRateWarning(false)}
      />
    </Overlay>
  );
};

export default connect(mapStateToProps, mapActionsToProps)(UserEditor);
